import type { Printing } from "@webscan/catalogue.js";
import { gallery } from "@webscan/retrieve.js";
import { captureKey } from "../../shared/captures";

/** Extra catalogue facts travel with the chosen printing through session persistence. */
export interface ReviewPrinting extends Printing {
  language?: string;
  nonfoil?: boolean;
  treatment?: string;
  image?: string;
  imageNormal?: string;
  oversized?: boolean;
}

interface Catalogue {
  dict: Record<string, string[]> & { set: Array<[string, string]> };
  printing: Record<string, Array<number | string | null>>;
}

let localCatalogue: unknown;
const localNames = new Map<string, ReviewPrinting[]>();
const remoteNames = new Map<string, ReviewPrinting[]>();

/** Every indexed printing of the name, including options absent from the shortlist. */
export function localPrintings(name: string): ReviewPrinting[] {
  if (localCatalogue !== gallery.catalogue) {
    localCatalogue = gallery.catalogue;
    localNames.clear();
  }
  const cached = localNames.get(name);
  if (cached) return cached;
  const catalogue = gallery.catalogue as Catalogue | null;
  if (!catalogue?.dict || !catalogue.printing) return [];
  const names = catalogue.dict.name;
  const nameId = names.indexOf(name);
  if (nameId < 0) return [];
  const columns = catalogue.printing;
  const results = new Map<string, ReviewPrinting>();
  const dict = (column: string, i: number): string =>
    catalogue.dict[column]?.[Number(columns[column]?.[i])] ?? "";
  const cents = (column: string, i: number): number | null => {
    const value = columns[column]?.[i];
    return typeof value === "number" ? value : null;
  };
  for (let i = 0; i < columns.name.length; i++) {
    if (columns.name[i] !== nameId) continue;
    const set = catalogue.dict.set[Number(columns.set[i])];
    if (!set) continue;
    const printing: ReviewPrinting = {
      name,
      setCode: set[0],
      setName: set[1],
      collector: String(columns.collector[i]),
      rarity: dict("rarity", i),
      artist: dict("artist", i),
      released: dict("released", i),
      scryfall: String(columns.scryfall[i] ?? ""),
      face: Number(columns.face[i] ?? 0),
      foil: !!columns.foil[i],
      frame: dict("frame", i),
      fullArt: !!columns.full_art?.[i],
      illustration: dict("illustration", i),
      usd: cents("usd", i),
      usdFoil: cents("usd_foil", i),
      eur: cents("eur", i),
      eurFoil: cents("eur_foil", i),
    };
    const key = captureKey(printing);
    const previous = results.get(key);
    if (!previous) results.set(key, printing);
    else {
      for (const key of ["usd", "usdFoil", "eur", "eurFoil"] as const) {
        if (printing[key] !== null) previous[key] = Math.max(previous[key] ?? 0, printing[key]);
      }
    }
  }
  const all = [...results.values()];
  localNames.set(name, all);
  return all;
}

export function cachedPrintings(name: string): ReviewPrinting[] | undefined {
  return remoteNames.get(name);
}

interface ScryfallFace {
  name: string;
  illustration_id?: string;
  artist?: string;
  image_uris?: { small?: string; normal?: string };
}

interface ScryfallCard extends ScryfallFace {
  id: string;
  set: string;
  set_name: string;
  collector_number: string;
  rarity: string;
  released_at: string;
  foil: boolean;
  nonfoil: boolean;
  frame: string;
  frame_effects?: string[];
  border_color?: string;
  full_art: boolean;
  oversized?: boolean;
  promo?: boolean;
  promo_types?: string[];
  lang: string;
  card_faces?: ScryfallFace[];
  prices: Record<string, string | null>;
}

function remotePrinting(card: ScryfallCard, name: string): ReviewPrinting {
  const faceIndex = card.card_faces?.findIndex((face) => face.name === name) ?? -1;
  const face = faceIndex >= 0 ? card.card_faces?.[faceIndex] : undefined;
  // A face's image data describes that face; never fill gaps with the root front image.
  const images = face?.image_uris ?? card.image_uris;
  const effects = card.frame_effects ?? [];
  const treatment =
    card.border_color === "borderless"
      ? "borderless"
      : effects.includes("extendedart")
        ? "extended art"
        : effects.includes("showcase")
          ? "showcase"
          : card.full_art
            ? "full art"
            : card.frame === "1993" || card.frame === "1997"
              ? "retro frame"
              : "standard frame";
  const notes = [treatment];
  if (card.promo_types?.includes("prerelease")) notes.push("date-stamped promo");
  else if (card.promo) notes.push("promo");
  if (card.oversized) notes.push("oversized");
  if (!card.nonfoil && card.foil) notes.push("foil only");
  const price = (key: string): number | null => {
    const raw = card.prices?.[key];
    if (raw == null) return null;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : null;
  };
  return {
    name,
    setCode: card.set,
    setName: card.set_name,
    collector: card.collector_number,
    rarity: card.rarity,
    artist: face?.artist ?? card.artist ?? "",
    released: card.released_at,
    scryfall: card.id,
    face: Math.max(0, faceIndex),
    foil: card.foil,
    nonfoil: card.nonfoil,
    frame: card.frame,
    fullArt: card.full_art,
    illustration: face?.illustration_id ?? card.illustration_id ?? "",
    usd: price("usd"),
    usdFoil: price("usd_foil"),
    eur: price("eur"),
    eurFoil: price("eur_foil"),
    language: card.lang,
    treatment: notes.join(" · "),
    oversized: card.oversized,
    image: images?.small ?? images?.normal,
    imageNormal: images?.normal,
  };
}

let lastRequest = 0;

/** Only an exact card name leaves the device. Captures are never uploaded. */
export async function allPrintings(name: string, signal: AbortSignal): Promise<ReviewPrinting[]> {
  const cached = remoteNames.get(name);
  if (cached) return cached;
  const query = new URL("https://api.scryfall.com/cards/search");
  query.search = new URLSearchParams({
    q: `!"${name.replace(/["\\]/g, "\\$&")}"`,
    unique: "prints",
    include_extras: "true",
    include_multilingual: "true",
    include_variations: "true",
    order: "released",
    dir: "desc",
  }).toString();
  let next: string | undefined = query.href;
  const results = new Map<string, ReviewPrinting>();
  const pages = new Set<string>();
  while (next) {
    signal.throwIfAborted();
    // Respect API pacing across pages and rapid changes of review step.
    const wait = Math.max(0, 110 - (Date.now() - lastRequest));
    if (wait) await new Promise<void>((resolve) => window.setTimeout(resolve, wait));
    signal.throwIfAborted();
    const url = new URL(next);
    if (url.origin !== "https://api.scryfall.com" || pages.has(url.href))
      throw new Error("The printing catalogue returned an invalid next page.");
    pages.add(url.href);
    lastRequest = Date.now();
    const response = await fetch(url, { signal, headers: { Accept: "application/json" } });
    if (!response.ok)
      throw new Error(`The printing catalogue is unavailable (${response.status}).`);
    const page = (await response.json()) as {
      data?: ScryfallCard[];
      has_more?: boolean;
      next_page?: string;
    };
    if (!Array.isArray(page.data)) throw new Error("The printing catalogue returned no list.");
    for (const card of page.data) {
      if (!card.id || !card.collector_number || !card.set) continue;
      if (card.name !== name && !card.card_faces?.some((face) => face.name === name)) continue;
      results.set(card.id, remotePrinting(card, name));
    }
    if (page.has_more && !page.next_page)
      throw new Error("The printing catalogue returned an incomplete list.");
    next = page.has_more ? page.next_page : undefined;
  }
  const printings = [...results.values()];
  if (remoteNames.size >= 16) remoteNames.delete(remoteNames.keys().next().value!);
  remoteNames.set(name, printings);
  return printings;
}

export function treatmentLabel(printing: ReviewPrinting): string {
  if (printing.treatment) return printing.treatment;
  if (printing.fullArt) return "full art";
  if (printing.frame === "1993" || printing.frame === "1997") return "retro frame";
  return "frame details unavailable";
}

export function languageLabel(code: string | undefined): string {
  if (!code || code === "en") return "";
  const names: Record<string, string> = {
    ja: "japanese",
    de: "german",
    fr: "french",
    es: "spanish",
    it: "italian",
    pt: "portuguese",
    ru: "russian",
    ko: "korean",
    zhs: "simplified chinese",
    zht: "traditional chinese",
    he: "hebrew",
    la: "latin",
    grc: "ancient greek",
    ar: "arabic",
    sa: "sanskrit",
    phy: "phyrexian",
    ph: "phyrexian",
  };
  return names[code] ?? code;
}
